import React, { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import ChatThread from "@/components/ChatThread";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useEntries } from "@/context/EntriesContext";
import { SESSIONS, MILESTONES, SUCCESS_MEASURES, CHECKPOINTS, INTAKE_FIELDS, COACH_WATCH, PILOT_RISKS } from "@/data/bridgeData";
import { DELIVERABLE_DOCS } from "@/data/activityTools";
import { CheckCircle2, Circle, ChevronDown, AlertTriangle, Download, Sparkles, MessageCircle, Star } from "lucide-react";

function ReadBlock({ label, value }) {
  if (!value) return null;
  return (
    <div className="mb-4">
      <div className="text-sm font-medium text-stone-900 mb-1">{label}</div>
      <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-stone-600">{value}</p>
    </div>
  );
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch (e) { return ""; }
}

function SharedHighlights({ participantId, name }) {
  const [shares, setShares] = useState(null);

  useEffect(() => {
    let active = true;
    api.get("/shares", { params: { participant_id: participantId } })
      .then((res) => { if (active) setShares(res.data || []); })
      .catch(() => { if (active) setShares([]); });
    return () => { active = false; };
  }, [participantId]);

  return (
    <div className="rounded-sm border border-brand-sage/40 bg-brand-sage/5 p-7">
      <div className="flex items-center gap-2 mb-4">
        <Star size={16} className="text-brand-sage" />
        <div className="label-eyebrow" style={{ color: "#697A76" }}>Shared by {name?.split(" ")[0] || "participant"}</div>
        {shares && shares.length > 0 && <span className="text-sm text-stone-400">{shares.length}</span>}
      </div>
      {shares === null ? (
        <p className="text-sm text-stone-400">Loading…</p>
      ) : shares.length === 0 ? (
        <p className="text-sm text-stone-500">Nothing shared yet. When {name?.split(" ")[0] || "they"} flags a reflection or synthesis, it appears here.</p>
      ) : (
        <div className="space-y-4">
          {shares.map((s) => (
            <div key={s.id} data-testid={`shared-item-${s.id}`} className="rounded-sm border border-stone-200 bg-white p-4">
              <div className="flex items-center justify-between gap-3 mb-1">
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-brand-parchment px-2 py-0.5 text-[10px] uppercase tracking-wide text-brand-primary">{s.kind === "synthesis" ? "AI synthesis" : "Reflection"}</span>
                  <span className="text-sm font-medium text-stone-900">{s.label}</span>
                </div>
                <span className="text-xs text-stone-400 shrink-0">{fmtDate(s.created_at)}</span>
              </div>
              {s.note && <p className="mb-2 text-sm italic text-stone-500">"{s.note}"</p>}
              <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-stone-700">{s.snapshot}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CoachParticipant() {
  const { user, activeParticipant } = useAuth();
  const { getEntry, saveEntry, loading } = useEntries();
  const navigate = useNavigate();
  const [open, setOpen] = useState(1);

  if (user?.role !== "coach") return <Navigate to="/studio" replace />;
  if (!activeParticipant) return <Navigate to="/coach" replace />;

  const msDone = MILESTONES.filter((m) => getEntry(`milestone:${m.n}:done`, false)).length;
  const scales = SUCCESS_MEASURES.filter((m) => m.type === "scale");

  return (
    <Layout coachContext>
      <div className="label-eyebrow mb-2">Participant studio</div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <h1 className="font-serif text-4xl sm:text-5xl font-light text-stone-900">{activeParticipant.name}</h1>
        <button
          data-testid="coach-export-btn"
          onClick={() => navigate("/export")}
          className="inline-flex items-center gap-2 rounded-sm border border-stone-300 px-4 py-2.5 text-sm text-stone-700 transition-all duration-300 hover:border-brand-primary hover:text-brand-primary"
        >
          <Download size={15} /> Export facilitator kit
        </button>
      </div>

      {/* Snapshot */}
      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        <div className="rounded-sm border border-stone-200 bg-white p-6">
          <div className="label-eyebrow mb-2">Milestones</div>
          <div className="font-serif text-4xl text-brand-primary">{msDone}<span className="text-stone-300">/6</span></div>
        </div>
        <div className="rounded-sm border border-stone-200 bg-white p-6">
          <div className="label-eyebrow mb-2">Conversations</div>
          <div className="font-serif text-4xl text-stone-900">{getEntry("measure:total:conversations_count", 0)}</div>
        </div>
        <div className="rounded-sm border border-stone-200 bg-white p-6">
          <div className="label-eyebrow mb-2">Proposals / pilots</div>
          <div className="font-serif text-4xl text-stone-900">{getEntry("measure:total:proposals_count", 0)}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-5">
          {/* Shared highlights from the participant */}
          <SharedHighlights participantId={activeParticipant.id} name={activeParticipant.name} />

          {/* Direct messages */}
          <div className="rounded-sm border border-stone-200 bg-white p-7">
            <div className="flex items-center gap-2 mb-4">
              <MessageCircle size={16} className="text-brand-primary" />
              <div className="label-eyebrow">Messages with {activeParticipant.name?.split(" ")[0]}</div>
            </div>
            <ChatThread participantId={activeParticipant.id} height="h-[360px]" />
          </div>

          {/* Intake summary */}
          <div className="rounded-sm border border-stone-200 bg-white p-7">
            <div className="label-eyebrow mb-4">Intake</div>
            {INTAKE_FIELDS.map((f) => (
              <ReadBlock key={f.id} label={f.label} value={getEntry(`intake:${f.id}`, "")} />
            ))}
            {!INTAKE_FIELDS.some((f) => getEntry(`intake:${f.id}`, "")) && (
              <p className="text-sm text-stone-400">No intake notes yet.</p>
            )}
          </div>

          {/* Deliverables produced */}
          {(() => {
            const produced = DELIVERABLE_DOCS.filter((d) => String(getEntry(d.key, "")).trim());
            return (
              <div className="rounded-sm border border-stone-200 bg-white p-7">
                <div className="flex items-center justify-between mb-4">
                  <div className="label-eyebrow">Deliverables produced</div>
                  <span className="text-sm text-stone-400">{produced.length}/{DELIVERABLE_DOCS.length}</span>
                </div>
                {produced.length === 0 ? (
                  <p className="text-sm text-stone-400">No deliverables drafted yet.</p>
                ) : (
                  produced.map((d) => (
                    <ReadBlock key={d.key} label={d.title} value={getEntry(d.key, "")} />
                  ))
                )}
              </div>
            );
          })()}

          {/* Sessions accordion with coach notes */}
          {SESSIONS.map((s) => {
            const isOpen = open === s.id;
            const noteKey = `coachnote:s${s.id}`;
            return (
              <div key={s.id} className="rounded-sm border border-stone-200 bg-white">
                <button
                  data-testid={`coach-session-toggle-${s.id}`}
                  onClick={() => setOpen(isOpen ? null : s.id)}
                  className="flex w-full items-center justify-between p-6"
                >
                  <span className="flex items-center gap-3">
                    {getEntry(`milestone:${s.milestone.n}:done`, false)
                      ? <CheckCircle2 size={18} className="text-brand-sage" />
                      : <Circle size={18} className="text-stone-300" />}
                    <span className="font-serif text-2xl text-stone-900">Session {s.id} – {s.title}</span>
                  </span>
                  <ChevronDown size={18} className={`text-stone-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-6 pb-6 border-t border-stone-100 pt-5">
                    <div className="label-eyebrow mb-3">Participant reflections</div>
                    {s.exercises.map((ex) => {
                      const resp = getEntry(`s${s.id}:ex:${ex.id}`, "");
                      const synth = getEntry(`s${s.id}:synth:${ex.id}`, "");
                      return (
                        <div key={ex.id} className="mb-4">
                          <ReadBlock label={ex.title} value={resp} />
                          {synth && (
                            <div className="rounded-sm border border-brand-sage/30 bg-brand-sage/10 p-3 text-sm text-stone-600 whitespace-pre-wrap">{synth}</div>
                          )}
                          {!resp && !synth && <p className="text-sm text-stone-400 mb-3">{ex.title}: –</p>}
                        </div>
                      );
                    })}

                    <div className="mt-5">
                      <div className="label-eyebrow mb-2" style={{ color: "#8C6B5D" }}>Coach notes</div>
                      <textarea
                        data-testid={`coach-note-${s.id}`}
                        value={getEntry(noteKey, "")}
                        onChange={(e) => saveEntry(noteKey, e.target.value)}
                        rows={3}
                        placeholder="Pattern recognition, challenge, what to hold next session…"
                        className="w-full resize-y rounded-sm border border-stone-200 bg-brand-parchment/30 p-3 text-stone-800 focus:outline-none focus:border-brand-primary transition-colors"
                      />
                      <div className="mt-4 label-eyebrow mb-2">Coach prompts</div>
                      <ul className="space-y-2">
                        {s.coachPrompts.map((p) => (
                          <li key={p} className="border-l-2 border-stone-200 pl-3 text-sm italic text-stone-500">"{p}"</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Side: scores + watch-fors */}
        <div className="space-y-8">
          <div className="rounded-sm border border-stone-200 bg-white p-6">
            <div className="label-eyebrow mb-4">Confidence shift</div>
            <div className="space-y-3">
              {scales.map((m) => (
                <div key={m.id}>
                  <div className="text-sm text-stone-600 mb-1">{m.label}</div>
                  <div className="flex gap-2">
                    {CHECKPOINTS.map((cp) => (
                      <div key={cp.id} className="flex-1 text-center rounded-sm border border-stone-200 py-1.5">
                        <div className="text-[10px] uppercase tracking-wider text-stone-400">{cp.label}</div>
                        <div className="font-serif text-lg text-stone-900">{getEntry(`measure:${cp.id}:${m.id}`, 0)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-sm border border-stone-200 bg-brand-parchment/40 p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle size={15} className="text-brand-accent" />
              <div className="label-eyebrow">Coach should watch for</div>
            </div>
            <ul className="space-y-2.5">
              {COACH_WATCH.map((w) => (
                <li key={w} className="text-sm text-stone-600 leading-snug">– {w}</li>
              ))}
            </ul>
          </div>

          <div className="rounded-sm border border-stone-200 bg-white p-6">
            <div className="label-eyebrow mb-4">Pilot risks & responses</div>
            <ul className="space-y-4">
              {PILOT_RISKS.map((r) => (
                <li key={r.title}>
                  <div className="text-sm font-medium text-stone-900 leading-snug">{r.title}</div>
                  <div className="text-xs text-[#9E4A41] mt-0.5">Watch: {r.watch}</div>
                  <div className="text-xs text-brand-sage mt-0.5">Response: {r.response}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Layout>
  );
}
